import classnames from 'classnames';
import React, { Component } from 'react';
import { func, string, bool, instanceOf } from 'prop-types';
import {
  CallEnd,
  ArrowDropUp,
  Mic,
  MicOff,
  Videocam as VideoCam,
  VideocamOff as VideoCamOff,
  VolumeUp,
  VolumeOff,
} from '@material-ui/icons';
import {
  ButtonGroup,
  Button,
  Divider,
  Tooltip,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Toolbar,
} from '@material-ui/core';

import Loader from 'Elements/Loader';
import { withAppContextHOC } from 'Scenes/_root/appContext';
import {
  withMeetingContext,
  MeetingAVInputStates,
  MeetingContextAVControlsProps,
  MeetingContextMeetingControlsProps,
} from 'Scenes/_root/meetingContext';
import meetingConstants from 'Services/constants/meetings';
import styles from './styles.scss';

class MediaControls extends Component {
  constructor(props) {
    super(props);
    this.qualitiesAnchorRef = null;
    const {
      avControls,
      registerMeetingCallbacks,
    } = props;
    const {
      audioIn,
      video,
      videoQuality,
    } = avControls.getState();
    this.state = {
      micState: audioIn,
      isMicLoading: false,
      camState: video,
      isCamLoading: false,
      videoQualityState: videoQuality,
      isVideoQualityLoading: false,
      isQualitiesOpen: false,
    };
    this.unregisterMeetingCallbacks = registerMeetingCallbacks('mediaControls', {
      onLocalMuteUpdated: this.onLocalMuteUpdated,
      onVideoQualityUpdated: this.onVideoQualityUpdated,
      onMeetingVideoTileUpdated: this.onMeetingVideoTileUpdated,
      onSelectedAudioInDeviceIdUpdated: this.onSelectedAudioInDeviceIdUpdated,
    });
  }

  componentWillUnmount() {
    this.unregisterMeetingCallbacks();
  }

  onSelectedAudioInDeviceIdUpdated = () => {
    const { avControls } = this.props;
    const { audioIn } = avControls.getState();
    this.setState({
      micState: audioIn,
    });
  }

  onLocalMuteUpdated = (isMuted) => {
    this.setState({
      micState: isMuted ? MeetingAVInputStates.OFF : MeetingAVInputStates.ON,
      isMicLoading: false,
    });
  }

  onVideoQualityUpdated = (qualityId) => {
    this.setState({
      videoQualityState: qualityId,
      isVideoQualityLoading: false,
    });
  }

  onMeetingVideoTileUpdated = (bindVideoElement, tileState) => {
    const { active, localTile } = tileState;
    if (localTile) {
      this.setState({
        camState: active ? MeetingAVInputStates.ON : MeetingAVInputStates.OFF,
        isCamLoading: false,
      });
    }
  }

  toggleMic = () => {
    const { avControls } = this.props;
    this.setState({
      isMicLoading: true,
    }, () => {
      if (this.state.micState === MeetingAVInputStates.ON) {
        avControls.audioIn.off();
      } else {
        avControls.audioIn.on();
      }
    });
  }

  toggleCam = () => {
    const { avControls } = this.props;
    this.setState({
      isCamLoading: true,
    }, () => {
      if (this.state.camState === MeetingAVInputStates.ON) {
        avControls.video.off();
      } else {
        avControls.video.on();
      }
    });
  }

  handleEndCallClick = () => {
    const {
      consultGuid,
      isOnDashboard,
      isLoggedIn,
      meetingControls,
      meetingEnd,
    } = this.props;
    if (isLoggedIn && isOnDashboard) {
      meetingEnd(consultGuid).then(() => {
        meetingControls.stop();
      });
    } else {
      meetingControls.stop();
    }
  }

  handleToggleQualities = () => {
    this.setState((prevState) => ({
      isQualitiesOpen: !prevState.isQualitiesOpen,
    }));
  }

  handleCloseQualities = (event) => {
    // if we're clicking inside the popper, dont close
    if (this.qualitiesAnchorRef.current && this.qualitiesAnchorRef.current.contains(event.target)) {
      return;
    }
    this.setState({
      isQualitiesOpen: false,
    });
  };

  handleVideoQualityChange = (qualityId) => {
    const {
      avControls,
    } = this.props;
    avControls.videoQuality.set(qualityId);
    this.setState({
      isQualitiesOpen: false,
    });
  }

  setQualityAnchorsRef = (ref) => {
    this.qualitiesAnchorRef = ref;
  }

  renderIcon = (isLoading, currentState, OnIcon, OffIcon, onText, offText) => {
    if (isLoading) {
      return <Loader size={24} />;
    } else if (currentState === MeetingAVInputStates.ON) {
      return (
        <Tooltip placement='top' title={onText}>
          <OnIcon />
        </Tooltip>
      );
    }
    return (
      <Tooltip placement='top' title={offText}>
        <OffIcon />
      </Tooltip>
    );
  }

  render() {
    const {
      micState,
      isMicLoading,
      isCamLoading,
      camState,
      videoQualityState,
      isVideoQualityLoading,
      isQualitiesOpen,
    } = this.state;
    const {
      isPortrait,
    } = this.props;

    return (
      <div className={classnames(
        'mediaControls',
        styles.mediaControlsWrapper,
        isPortrait && styles.mediaControlsPortraitWrapper
      )}>
        { this.qualitiesAnchorRef && (
          <Popper
            anchorEl={this.qualitiesAnchorRef}
            disablePortal
            open={isQualitiesOpen}
            placement='top-end'
            transition
          >
            {({ TransitionProps }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={this.handleCloseQualities}>
                    <MenuList>
                      {meetingConstants.qualitiesOrder.map((qualityId) => (
                        <MenuItem
                          key={qualityId}
                          disabled={isVideoQualityLoading}
                          onClick={() => this.handleVideoQualityChange(qualityId)}
                          selected={qualityId === videoQualityState}
                        >
                          {meetingConstants.qualities[qualityId].label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        )}
        <Paper square>
          <Toolbar classes={{ root: styles.toolbar }} disableGutters>
            <ButtonGroup
              color={camState === MeetingAVInputStates.ON ? 'primary' : 'inherit'}
              disabled={isCamLoading || camState === MeetingAVInputStates.DISABLED}
              variant='text'
            >
              <Button
                onClick={this.toggleCam}
              >
                {this.renderIcon(isCamLoading, camState, VideoCam, VideoCamOff, 'Disable camera', 'Enable camera')}
              </Button>
              <Button
                ref={this.setQualityAnchorsRef}
                disabled={isVideoQualityLoading}
                onClick={this.handleToggleQualities}
                size='small'
              >
                <ArrowDropUp />
              </Button>
            </ButtonGroup>
            <Button
              color={micState === MeetingAVInputStates.ON ? 'primary' : 'inherit'}
              disabled={isMicLoading || micState === MeetingAVInputStates.DISABLED}
              onClick={this.toggleMic}
            >
              {this.renderIcon(isMicLoading, micState, Mic, MicOff, 'Mute microphone', 'Unmute microphone')}
            </Button>
            <Divider flexItem orientation='vertical' />
            <Button
              className='end-call'
              onClick={this.handleEndCallClick}
            >
              <Tooltip placement='top' title='End call'>
                <CallEnd />
              </Tooltip>
            </Button>
          </Toolbar>
        </Paper>
      </div>
    );
  }
}

const MapStateProps = {
  isEndingMeeting: bool.isRequired,
  isOnDashboard: bool.isRequired,
  consultGuid: string.isRequired,
};

const MapDispatchProps = {
  meetingEnd: func.isRequired,
};

const AppContextProps = {
  isLoggedIn: bool.isRequired,
};

MediaControls.propTypes = {
  audioOutElement: instanceOf(Element),
  isPortrait: bool.isRequired,
  localVideoTile: instanceOf(Element),
  avControls: MeetingContextAVControlsProps,
  meetingControls: MeetingContextMeetingControlsProps,
  registerMeetingCallbacks: func.isRequired,
  ...MapStateProps,
  ...MapDispatchProps,
  ...AppContextProps,
};


export default withMeetingContext([
  'avControls',
  'registerMeetingCallbacks',
  'meetingControls',
])(withAppContextHOC(['isLoggedIn'])(MediaControls));
